// Global script Runs before any components are loaded
import { determineDeviceType } from '../utils';

type AllBamTags = Extract<keyof HTMLElementTagNameMap, `bam-${string}`>;

type UnionToKeys<U extends string | symbol> = { [K in U]: '' };

const BamTagMap: UnionToKeys<AllBamTags> = {
    'bam-banner': '',
    'bam-footer': '',
    'bam-image': '',
    'bam-inline-promotion': '',
    'bam-inline-promotion-block': '',
    'bam-page-blocker': '',
    'bam-promotion-list': '',
    'bam-sticky-cta': '',
    'bam-content-blocker': '',
    'bam-compliance': '',
    'bam-odds-widget': '',
    'bam-odds-table': '',
    'bam-odds-strip': '',
    'bam-bonus-promotion': '',
    'bam-field': '',
    'bam-button': '',
    'bam-odds': '',
    'bam-recommend': '',
    'bam-casino-banner': '',
    'bam-large-sticky-cta': '',
    'bam-email-capture': '',
    'bam-bonus-card': '',
    'bam-rating': '',
    'bam-top-ten': '',
    'bam-recirc-logo': '',
    'bam-promo-overlay': '',
    'bam-card-carousel': '',
    'bam-trending-parlays': '',
    'bam-single-parlays': '',
    'bam-three-props': '',
    'bam-expert-picks': '',
    'bam-expert-pick-link': '',
    'bam-odds-expert-pick': '',
};

const snippetTags = Object.keys(BamTagMap);

const snippetAttributes = [
    'data-placement-id',
    'data-property-id',
    'data-promotion-id',
    'data-context',
    'data-location',
    'data-country-code',
    'data-page-slug',
    'data-subscription-tier',
    'data-was-pro',
    'data-subdivision',
    'data-league',
    'data-game-id',
    'data-cta-type',
    'data-is-mobile',
    'data-device-type',
    'data-is-betsync-user',
    'data-defer',
    'data-dev-mode',
    'data-max-length',
    'data-module-title',
    'data-underline',
    'data-link',
    'data-affiliate',
    'data-affiliate-type',
    'data-internal-id',
    'data-identifiers',
    'data-format',
    'data-age',
    'data-hide-underage',
    'data-dark-mode',
    'data-background',
    'data-padding',
    'data-button-text',
    'data-field',
    'data-subtitle-text',
    'data-display-action',
    'data-entity',
    'data-entity-type',
    'data-sport',
    'data-position',
    'data-player-name',
    'data-entity-data',
    'data-show-banner',
    'data-banner-context',
    'data-deep-link',
    'data-deeplinks',
    'data-branding',
];

let bamLoaded = false;
let hasMutationObserver = true;
let observer = null;

if ('MutationObserver' in window) {
    observer = new MutationObserver(() => {
        // eslint-disable-next-line no-console
        console.debug('BAM - MutationObserver');
        if (checkBamDivs()) {
            initBAM();
        } else {
            // eslint-disable-next-line no-console
            console.debug('BAM - MutationObserver disconnecting');
            observer.disconnect();
        }
        bamLoaded = true;
    });
    observer.observe(document, {
        subtree: true,
        attributes: true,
    });
} else {
    hasMutationObserver = false;
}

function initBAM() {
    if (bamLoaded && hasMutationObserver) {
        observer.disconnect();
        return; //
    }
    // BAM Fields - set initial value for any server rendered fields for default/initial values
    const fields = document.querySelectorAll('bam-field');
    for (let i = 0; i < fields.length; i++) {
        for (let j = 0; j < fields[i].childNodes.length; j++) {
            const child = fields[i].childNodes[j];
            let text = '';
            if (child.nodeName === '#text') {
                text = child.nodeValue.trim();
            }
            if (child.nodeName === 'A') {
                const atag = child as HTMLElement;
                text = atag.outerHTML.replace('"', '&quot;');
            }
            if (text !== '') {
                fields[i].setAttribute('initial-value', encodeURI(text));
            }
        }
    }

    const deviceType = determineDeviceType();

    const convertAttributes = (element: Element) => {
        const attrs = Array.from(element.attributes);
        attrs.forEach(attr => {
            if (attr.name.startsWith('data-') && snippetAttributes.includes(attr.name)) {
                const newAttrName = attr.name.replace('data-', '');
                element.setAttribute(newAttrName, attr.value);
                element.removeAttribute(attr.name);
            }
        });
        // TODO if device type is set on the component, don't override it
        element.setAttribute('device-type', deviceType);
    };

    // STEP 1
    // look for global styles and insert if they don't exist
    const globalStyles = document.querySelectorAll('link[rel="stylesheet"][href*="bam-scripts.css"]');
    if (globalStyles.length === 0) {
        const globalStyle = document.createElement('link');
        globalStyle.setAttribute('rel', 'stylesheet');
        globalStyle.setAttribute('href', 'https://b-static.bet-links.com/0.0.2/bam-scripts/bam-scripts.css');
        document.head.appendChild(globalStyle);
    }

    // STEP 2
    // find all snippet tags and remove data- from attributes used by the snippets
    const snippetElements = document.querySelectorAll(snippetTags.join(','));
    snippetElements.forEach(element => {
        convertAttributes(element);
    });

    // STEP 3
    // find all divs with snippet classnames and
    // change them to the correct tag w/ correct attributes
    snippetTags.forEach(tag => {
        const divs = document.querySelectorAll(`div.${tag}`);
        divs.forEach(div => {
            const newTag = document.createElement(tag);
            // copy over all attributes
            for (let i = 0; i < div.attributes.length; i++) {
                const attr = div.attributes[i];
                newTag.setAttribute(attr.name, attr.value);
            }

            convertAttributes(newTag);

            div.parentNode.replaceChild(newTag, div);
        });
    });
}

function checkBamDivs() {
    let toReturn = false;
    snippetTags.forEach(tag => {
        const divs = document.querySelectorAll(`div.${tag}`);
        if (divs.length > 0) {
            toReturn = true;
        }
    });
    return toReturn;
}

type TCLElement =
    | 'tcl-live-event-odds'
    | 'tcl-playbook'
    | 'tcl-prediction-market'
    | 'tcl-public-betting'
    | 'tcl-prop-projections';

const tclElements: TCLElement[] = [
    'tcl-live-event-odds',
    'tcl-playbook',
    'tcl-prediction-market',
    'tcl-public-betting',
    'tcl-prop-projections',
];

function hasTclElements(): boolean {
    const selectors = tclElements.flatMap(tag => [tag, `div.${tag}`]);
    return document.querySelector(selectors.join(',')) !== null;
}

function loadTanComponentLibrary() {
    if (document.getElementById('tan-components')) {
        return;
    }
    const script = document.createElement('script');
    script.id = 'tan-components';
    script.type = 'module';
    script.defer = true;
    script.src = 'https://d3lytooc21rae5.cloudfront.net/current/tan-component-library/tan-component-library.esm.js';
    document.head.appendChild(script);
}

if (typeof window !== 'undefined') {
    initBAM();

    if (hasTclElements()) {
        loadTanComponentLibrary();
    }
}
